import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useCofinancementData, useGenerateCofinancementSummaryPdf } from '../hooks/useApiQueries';
import theme from '../theme';
import { useAuth } from './AuthContext';

interface CofinancementData {
  id: number;
  deal_id: number;
  estimated_selling_price: number;
  client_desired_price?: number;
  estimated_rotation_weeks: number;
  encheres_vo_price?: number;
  purchase_price?: number;
  status: 'pending' | 'client_validated' | 'skipcar_validated' | 'rejected';
  client_validated: boolean;
  skipcar_validated: boolean;
  validated_at?: string;
  skipcar_validated_at?: string;
  summary_pdf_url?: string;
  created_at: string;
  updated_at: string;
  vehicle_info?: {
    make: string;
    model: string;
    registration_plate: string;
  };
}

interface CommissionRow {
  week: number;
  rate: number;
  amount: number;
  cumulative: number;
}

const Cofinancement: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const [validating, setValidating] = useState(false);
  const [sliderWeeks, setSliderWeeks] = useState(1);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [estimatedSellingPrice, setEstimatedSellingPrice] = useState<number>(0);
  const [clientDesiredPrice, setClientDesiredPrice] = useState<number>(0);
  const [displayWeeks, setDisplayWeeks] = useState(1);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [purchasePrice, setPurchasePrice] = useState<number>(0);
  const [hasModifications, setHasModifications] = useState(false);
  const [updatingFields, setUpdatingFields] = useState(false);
  const [participationHT, setParticipationHT] = useState<number>(0);
  const [adminUpdating, setAdminUpdating] = useState(false);

  const { data: cofinancementResponse, isLoading: loading, error, refetch } = useCofinancementData(dealId || '');
  const cofinancementData = (cofinancementResponse as any)?.data as CofinancementData | null;

  const { refetch: generatePdf } = useGenerateCofinancementSummaryPdf(dealId || '');

  useEffect(() => {
    if (cofinancementData) {
      setSliderWeeks(cofinancementData.estimated_rotation_weeks);
      setDisplayWeeks(cofinancementData.estimated_rotation_weeks);
      const dbPurchasePrice = Number(cofinancementData.purchase_price) || 0;
      const dbClientDesiredPrice = Number(cofinancementData.client_desired_price) || 0;
      setEstimatedSellingPrice(Number(cofinancementData.estimated_selling_price) || 0);
      setClientDesiredPrice(dbClientDesiredPrice);
      setPurchasePrice(dbPurchasePrice);
      setParticipationHT(dbClientDesiredPrice - dbPurchasePrice);
      setHasModifications(false);
    }
  }, [cofinancementData]);

  const calculateCommissions = (weeksToCalculate: number = displayWeeks): CommissionRow[] => {
    if (!cofinancementData) return [];

    const priceSKP = purchasePrice || 0;
    const commissions: CommissionRow[] = [];
    let cumulative = 0;

    for (let week = 1; week <= weeksToCalculate; week++) {
      let rate = 0;

      if (week <= 4) {
        rate = 0.015; // 1.5%
      } else if (week <= 8) {
        rate = 0.01; // 1%
      } else if (week <= 12) {
        rate = 0.0075; // 0.75%
      } else {
        rate = 0.005; // 0.5%
      }

      const amount = priceSKP * rate;
      cumulative += amount;

      commissions.push({
        week,
        rate: rate * 100,
        amount,
        cumulative,
      });
    }

    return commissions;
  };

  const handleValidateCofinancement = async () => {
    try {
      setValidating(true);

      // Déterminer qui valide selon le statut actuel
      const validatedBy = cofinancementData?.status === 'pending' ? 'client' : 'skipcar';

      await makeAuthenticatedApiCall('put', `/api/cofinancement/${dealId}/validate`, {
        validatedBy,
        estimated_rotation_weeks: sliderWeeks,
        client_desired_price: purchasePrice + participationHT,
        purchase_price: purchasePrice,
      });

      // Rafraîchir les données
      await refetch();
      setOpenConfirmDialog(false);
    } catch (error) {
      console.error('Error validating cofinancement:', error);
      // L'erreur sera gérée par React Query
    } finally {
      setValidating(false);
    }
  };

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    const weeks = newValue as number;
    setSliderWeeks(weeks);
    setHasModifications(true);

    // Quand on bouge la réglette, on refocalise sur cette valeur
    setDisplayWeeks(weeks);
  };

  const handleViewMore = () => {
    // Toujours montrer au moins jusqu'à sliderWeeks
    const minDisplay = Math.max(sliderWeeks, 1);

    if (displayWeeks < 24) {
      setDisplayWeeks(Math.max(24, minDisplay));
    } else if (displayWeeks < 56) {
      setDisplayWeeks(56);
    }
  };

  const handleViewLess = () => {
    // Toujours montrer au moins jusqu'à sliderWeeks
    const minDisplay = Math.max(sliderWeeks, 1);

    if (displayWeeks === 56) {
      setDisplayWeeks(Math.max(24, minDisplay));
    } else if (displayWeeks === 24) {
      setDisplayWeeks(minDisplay);
    }
  };

  const handleDownloadSummary = async () => {
    try {
      setGeneratingPdf(true);

      // Si le PDF existe déjà, l'ouvrir directement
      if (cofinancementData?.summary_pdf_url) {
        window.open(cofinancementData.summary_pdf_url, '_blank');
        setGeneratingPdf(false);
        return;
      }

      // Sinon, générer le PDF
      const result = await generatePdf();

      if (result.data && typeof result.data === 'object' && result.data !== null && 'pdfUrl' in result.data) {
        // Ouvrir le PDF dans un nouvel onglet
        window.open((result.data as any).pdfUrl, '_blank');

        // Rafraîchir les données pour mettre à jour l'URL du PDF
        await refetch();
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleUpdateAllFields = async () => {
    try {
      setUpdatingFields(true);

      await makeAuthenticatedApiCall('put', `/api/cofinancement/${dealId}/update-purchase-price`, {
        purchase_price: purchasePrice,
        client_desired_price: clientDesiredPrice,
        estimated_selling_price: estimatedSellingPrice,
        estimated_rotation_weeks: sliderWeeks,
      });

      // Rafraîchir les données
      await refetch();
      setHasModifications(false);
    } catch (error) {
      console.error('Error updating cofinancement fields:', error);
    } finally {
      setUpdatingFields(false);
    }
  };

  const handlePurchasePriceChange = (newPrice: number) => {
    setPurchasePrice(newPrice);
    setParticipationHT(clientDesiredPrice - newPrice);
    setHasModifications(true);
  };

  const handleAdminUpdateAndResendEnvelope = async () => {
    try {
      setAdminUpdating(true);

      await makeAuthenticatedApiCall('put', `/api/cofinancement/${dealId}/admin-update`, {
        purchase_price: purchasePrice,
        client_desired_price: clientDesiredPrice,
        estimated_selling_price: estimatedSellingPrice,
        estimated_rotation_weeks: sliderWeeks,
      });

      await refetch();
      setHasModifications(false);
    } catch (err) {
      console.error('Error during admin cofinancement update:', err);
    } finally {
      setAdminUpdating(false);
    }
  };

  const isGod = userRole === 'god';
  const isSkipcarValidated = cofinancementData?.status === 'skipcar_validated';
  const canGodEditAfterValidation = isGod && isSkipcarValidated;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'client_validated':
        return 'info';
      case 'skipcar_validated':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'En attente';
      case 'client_validated':
        return 'Validé par le client';
      case 'skipcar_validated':
        return 'Validé par SKIPCAR';
      case 'rejected':
        return 'Rejeté';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !cofinancementData) {
    return (
      <Box p={3}>
        <Alert severity="error">{error?.message || 'Données de cofinancement non trouvées'}</Alert>
        <Button variant="contained" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Retour
        </Button>
      </Box>
    );
  }

  // Quand le statut n'est plus "pending", on fixe le calcul sur estimated_rotation_weeks
  const weeksForFees = cofinancementData.status === 'pending' ? displayWeeks : sliderWeeks;
  const commissions = calculateCommissions(weeksForFees);
  const displayCommissions = calculateCommissions(displayWeeks);
  const totalFees = commissions[commissions.length - 1]?.cumulative || 0;

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        {/* En-tête */}
        <Grid item xs={5}>
          <Card>
            <CardContent>
              <Typography variant="h4" gutterBottom>
                {' '}
                Skipcar+
              </Typography>
              <Typography variant="h4" gutterBottom>
                {cofinancementData.vehicle_info && (
                  <span style={{ fontSize: '0.7em', fontWeight: 'normal', color: 'text.secondary' }}>
                    {cofinancementData.vehicle_info.make} - {cofinancementData.vehicle_info.model} -{' '}
                    {cofinancementData.vehicle_info.registration_plate}
                  </span>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ceci est une simulation, en aucun cas SKIPCAR s'engage sur l'acceptation du dossier, sur la date de
                vente, ou encore le prix final du véhicule
              </Typography>

              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <Typography variant="h6">Statut :</Typography>
                <Chip
                  label={getStatusLabel(cofinancementData.status)}
                  color={getStatusColor(cofinancementData.status) as any}
                />
              </Box>

              <Button
                className="secondaryDealActionButton"
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{ mr: 2 }}
              >
                Retour
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Colonne de gauche - Prix et marges */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Prix et marges
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Prix de couverture SKIPCAR
                  </Typography>
                  {userRole !== 'agency' &&
                  (cofinancementData.status === 'client_validated' ||
                    (cofinancementData.status === 'pending' && userRole === 'god') ||
                    canGodEditAfterValidation) ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextField
                        type="number"
                        value={purchasePrice}
                        onChange={(e) => handlePurchasePriceChange(Number(e.target.value))}
                        size="small"
                        sx={{ mt: 1, flex: 1 }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && hasModifications) {
                            if (canGodEditAfterValidation) {
                              handleAdminUpdateAndResendEnvelope();
                            } else {
                              handleUpdateAllFields();
                            }
                          }
                        }}
                      />
                      <Tooltip
                        title={
                          canGodEditAfterValidation
                            ? hasModifications
                              ? "Mettre à jour et renvoyer l'enveloppe à l'agence"
                              : "Modifier les champs puis renvoyer l'enveloppe"
                            : hasModifications
                              ? "Valider tous les champs et envoyer l'email"
                              : 'Modifier les champs puis valider'
                        }
                      >
                        <span>
                          <IconButton
                            size="medium"
                            onClick={
                              canGodEditAfterValidation ? handleAdminUpdateAndResendEnvelope : handleUpdateAllFields
                            }
                            disabled={!hasModifications || updatingFields || adminUpdating}
                            sx={{
                              mt: 1,
                              opacity: hasModifications ? 1 : 0.3,
                              transition: 'opacity 0.2s',
                              color: theme.palette.saffron.main,
                              '&:hover': {
                                backgroundColor: 'rgba(253, 187, 95, 0.1)',
                              },
                            }}
                          >
                            {updatingFields || adminUpdating ? (
                              <CircularProgress size={24} sx={{ color: theme.palette.saffron.main }} />
                            ) : (
                              <SendIcon fontSize="medium" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography variant="body1" fontWeight="bold">
                      {purchasePrice?.toLocaleString('fr-FR')} €
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Prix désiré par le client
                  </Typography>
                  <TextField
                    type="number"
                    value={clientDesiredPrice}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : Number(e.target.value);
                      setClientDesiredPrice(val);
                      setParticipationHT(val - purchasePrice);
                      setHasModifications(true);
                    }}
                    disabled={isSkipcarValidated && !isGod}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Participation frais d'achat agence HT
                  </Typography>
                  {cofinancementData?.status === 'pending' ||
                  (cofinancementData?.status === 'client_validated' && userRole === 'god') ||
                  canGodEditAfterValidation ? (
                    <TextField
                      type="number"
                      value={participationHT}
                      onChange={(e) => {
                        const val = e.target.value === '' ? 0 : Number(e.target.value);
                        setParticipationHT(val);
                        setClientDesiredPrice(purchasePrice + val);
                        setHasModifications(true);
                      }}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  ) : (
                    <Typography variant="body1" fontWeight="bold">
                      {(clientDesiredPrice - purchasePrice).toLocaleString('fr-FR')} €
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Participation frais d'achat agence TTC
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {((clientDesiredPrice - purchasePrice) * 1.2).toLocaleString('fr-FR')} €
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Prix de vente estimé
                  </Typography>
                  <TextField
                    type="number"
                    value={estimatedSellingPrice}
                    onChange={(e) => {
                      setEstimatedSellingPrice(Number(e.target.value));
                      setHasModifications(true);
                    }}
                    disabled={isSkipcarValidated && !isGod}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Marge agence
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color={estimatedSellingPrice - clientDesiredPrice >= 0 ? 'success.main' : 'error.main'}
                  >
                    {(estimatedSellingPrice - clientDesiredPrice).toLocaleString('fr-FR')} €
                  </Typography>
                </Grid>

                {/* Bouton de validation côté Skipcar */}
                {cofinancementData.status === 'client_validated' && userRole !== 'agency' && (
                  <Grid item xs={12}>
                    <Box mt={2} display="flex" justifyContent="center">
                      <Button
                        variant="contained"
                        className="dealActionButton"
                        color="success"
                        size="large"
                        onClick={() => setOpenConfirmDialog(true)}
                        disabled={validating}
                        startIcon={validating ? <CircularProgress size={20} color="inherit" /> : undefined}
                      >
                        {validating ? 'Validation en cours...' : 'Valider côté Skipcar'}
                      </Button>
                    </Box>
                  </Grid>
                )}

                {/* Bouton de téléchargement du récapitulatif */}
                {cofinancementData.status === 'skipcar_validated' && (
                  <Grid item xs={12} sx={{ display: 'none' }}>
                    <Box mt={2} display="flex" justifyContent="center">
                      <Button
                        variant="contained"
                        className="dealActionButton"
                        color="primary"
                        size="large"
                        onClick={handleDownloadSummary}
                        disabled={generatingPdf}
                        startIcon={generatingPdf ? <CircularProgress size={20} color="inherit" /> : undefined}
                      >
                        {generatingPdf ? 'Génération en cours...' : 'Télécharger le récapitulatif'}
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Commission de portage de stock - 100% de largeur */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Commission de portage de stock
              </Typography>

              <Box mb={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Ajuster le nombre de semaines de rotation : {sliderWeeks} semaine{sliderWeeks > 1 ? 's' : ''}
                  {cofinancementData?.status === 'skipcar_validated' && !isGod && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1, fontStyle: 'italic' }}
                    >
                      ⚠️ La rotation a été validée par Skipcar et ne peut plus être modifiée
                    </Typography>
                  )}
                </Typography>
                <Slider
                  value={sliderWeeks}
                  onChange={handleSliderChange}
                  min={1}
                  max={24}
                  step={1}
                  disabled={isSkipcarValidated && !isGod}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 4, label: '4' },
                    { value: 8, label: '8' },
                    { value: 12, label: '12' },
                    { value: 16, label: '16' },
                    { value: 20, label: '20' },
                    { value: 24, label: '24' },
                  ]}
                  valueLabelDisplay="auto"
                  sx={{
                    mt: 2,
                    '& .MuiSlider-track': {
                      backgroundColor: theme.palette.saffron.main,
                    },
                    '& .MuiSlider-thumb': {
                      backgroundColor: theme.palette.saffron.main,
                      '&:hover': {
                        backgroundColor: theme.palette.saffron.dark,
                      },
                      '&.Mui-focusVisible': {
                        backgroundColor: theme.palette.saffron.dark,
                      },
                      '&.Mui-active': {
                        backgroundColor: theme.palette.saffron.dark,
                      },
                    },
                    '& .MuiSlider-mark': {
                      backgroundColor: theme.palette.saffron.main,
                    },
                    '& .MuiSlider-markActive': {
                      backgroundColor: theme.palette.saffron.main,
                    },
                    '& .MuiSlider-valueLabel': {
                      backgroundColor: theme.palette.saffron.main,
                      color: '#000000',
                      fontWeight: 'bold',
                    },
                  }}
                />
              </Box>

              <Box mt={2}>
                <Alert severity="info">
                  D'après votre estimation de délai de vente, les frais SKIPCAR{' '}
                  {cofinancementData.status === 'pending' ? "s'évalueraient" : "s'évaluent"} à{' '}
                  <strong>{totalFees.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €</strong>
                </Alert>

                {cofinancementData.status === 'pending' && (
                  <Box mt={2} display="flex" justifyContent="center">
                    <Button
                      variant="contained"
                      className="dealActionButton"
                      color="primary"
                      size="large"
                      onClick={() => setOpenConfirmDialog(true)}
                      disabled={validating}
                      startIcon={validating ? <CircularProgress size={20} color="inherit" /> : undefined}
                    >
                      {validating ? 'Validation en cours...' : 'Valider cette demande de cofinancement'}
                    </Button>
                  </Box>
                )}

                {cofinancementData.status === 'client_validated' && (
                  <Box mt={2} display="flex" justifyContent="center">
                    <Typography variant="body1" color="success.main" sx={{ fontWeight: 'bold' }}>
                      {userRole === 'agency'
                        ? `Demande de cofinancement validée le ${new Date(cofinancementData.validated_at || '').toLocaleDateString('fr-FR')}`
                        : `Demande de cofinancement validée par l'agence le ${new Date(cofinancementData.validated_at || '').toLocaleDateString('fr-FR')}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tableau des commissions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Détail des commissions par semaine
              </Typography>

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Semaine</TableCell>
                      {userRole !== 'agency' && <TableCell align="right">Taux</TableCell>}
                      <TableCell align="right">Montant HT</TableCell>
                      <TableCell align="right">Montant HT cumulé</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayCommissions.map((commission) => {
                      const isHighlighted =
                        cofinancementData?.status === 'skipcar_validated' && commission.week === sliderWeeks;
                      return (
                        <TableRow
                          key={commission.week}
                          sx={{
                            backgroundColor: isHighlighted ? theme.palette.saffron.light : 'transparent',
                            '&:hover': {
                              backgroundColor: isHighlighted ? theme.palette.saffron.light : theme.palette.action.hover,
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: isHighlighted ? 'bold' : 'normal' }}>
                            {commission.week}
                            {isHighlighted && ' ✓'}
                          </TableCell>
                          {userRole !== 'agency' && (
                            <TableCell align="right" sx={{ fontWeight: isHighlighted ? 'bold' : 'normal' }}>
                              {commission.rate.toFixed(2)}%
                            </TableCell>
                          )}
                          <TableCell align="right" sx={{ fontWeight: isHighlighted ? 'bold' : 'normal' }}>
                            {commission.amount.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: isHighlighted ? 'bold' : 'normal' }}>
                            {commission.cumulative.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Boutons de contrôle d'affichage - masqués si validé par Skipcar */}
              {cofinancementData?.status !== 'skipcar_validated' && (
                <Box mt={2} display="flex" justifyContent="center" gap={2}>
                  {displayWeeks < 56 && (
                    <Button
                      className="secondaryDealActionButton"
                      variant="outlined"
                      onClick={handleViewMore}
                      size="small"
                    >
                      Voir Plus ({displayWeeks < 24 ? '24 semaines' : '56 semaines'})
                    </Button>
                  )}
                  {displayWeeks > Math.max(sliderWeeks, 1) && (
                    <Button
                      className="secondaryDealActionButton"
                      variant="outlined"
                      onClick={handleViewLess}
                      size="small"
                    >
                      Voir Moins (
                      {displayWeeks === 56
                        ? Math.max(24, sliderWeeks) === 24
                          ? '24 semaines'
                          : `${sliderWeeks} semaines`
                        : `${sliderWeeks} semaine${sliderWeeks > 1 ? 's' : ''}`}
                      )
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Modale de confirmation */}
      <Dialog
        open={openConfirmDialog}
        onClose={validating ? undefined : () => setOpenConfirmDialog(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        disableEscapeKeyDown={validating}
      >
        <DialogTitle id="confirm-dialog-title">
          {cofinancementData?.status === 'pending'
            ? 'Confirmation de la demande de cofinancement'
            : "Confirmation de l'approbation côté Skipcar"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            <Typography variant="body1" gutterBottom>
              {cofinancementData?.status === 'pending'
                ? 'Êtes-vous sûr de vouloir valider cette demande de cofinancement ?'
                : 'Êtes-vous sûr de vouloir approuver cette demande de cofinancement côté Skipcar ?'}
            </Typography>
            <Typography variant="body1" color="black" sx={{ mt: 2 }}>
              <strong>Récapitulatif :</strong>
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li style={{ color: 'black', fontWeight: 'normal' }}>
                Rotation estimée : {sliderWeeks} semaine{sliderWeeks > 1 ? 's' : ''}
              </li>
              <li style={{ color: 'black', fontWeight: 'normal' }}>
                Prix de couverture SKIPCAR : {purchasePrice?.toLocaleString('fr-FR')} €
              </li>
              <li style={{ color: 'black', fontWeight: 'normal' }}>
                Prix désiré par le client : {clientDesiredPrice?.toLocaleString('fr-FR')} €
              </li>
              <li style={{ color: 'black', fontWeight: 'normal' }}>
                Frais SKIPCAR estimés : {totalFees.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €
              </li>
            </Box>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Cette action validera définitivement votre demande de cofinancement.
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className="secondaryDealActionButton"
            onClick={() => setOpenConfirmDialog(false)}
            disabled={validating}
          >
            Annuler
          </Button>
          <Button
            className="dealActionButton"
            onClick={handleValidateCofinancement}
            variant="contained"
            color="primary"
            disabled={validating}
            startIcon={validating ? <CircularProgress size={16} color="inherit" /> : undefined}
            autoFocus
          >
            {validating
              ? 'Validation...'
              : cofinancementData?.status === 'pending'
                ? 'Confirmer la validation'
                : "Confirmer l'approbation"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Cofinancement;
